import React, { useState, useEffect, useCallback } from "react";
import { t } from "i18next";
import httpClient from "../../../lib/httpClient";
import { Grid, FormControlLabel, Checkbox } from "@material-ui/core";
import VisibilityOffRoundedIcon from "@material-ui/icons/VisibilityOffRounded";
import VisibilityRoundedIcon from "@material-ui/icons/VisibilityRounded";
import {
  GridFilterModel,
  GridValueGetterParams,
  GridValueFormatterParams,
  GridPaginationModel,
  GridSortModel,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";

import {
  tableCellRender,
  stringFilterOperators,
  isSingleSelectOperators,
  getLatestUpdateDaily,
  numberFilterOperators,
  fixedFormatter,
  buildingCompanyColumnFeatures,
  getCompanyCodeName,
} from "../../util/tableUtils";
import { initialFilterModel, isThereAnyIncompleteFilter } from "../../util/tableUtilsTS";
import { goToPolicyPage } from "./../../policy/utils";
import { DataGridCystellarTable } from "../../util/DataGridCystellarTable";
import { apiError } from "../../../lib/apiError";

import { getUserPermissionsList } from "../../util/permissionsUtils";
import { DownloadTableButton } from "../../util/DownloadTableButton";
import { getFilterQuery, getSortDetailsString } from "../../emergencyMonitoring/emergencyUtils";
import { useClientConfig } from "../../../context/clientConfigContext";
import { PermissionIdEnum } from "../../util/types/permissions/permissionIdEnum";

interface EmergencyPoliciesProps {
  activationCode: string;
  setSelectedPolicyList: Function;
  selectedPolicyList: EmergencyPolicy[];
}

interface RowData {
  _id: string;
  row: { policyNumber: string; policyNumberSecondary: string; multilocationID: string };
  // Other fields...
}

export const EmergencyPolicies: React.FC<EmergencyPoliciesProps> = ({
  activationCode,
  setSelectedPolicyList,
  selectedPolicyList,
}) => {
  const config = useClientConfig();

  const [policiesData, setPoliciesData] = useState<EmergencyPolicy[]>([]);
  const [tableState, setTableState] = useState({
    isLoading: false,
    total: 0,
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [lastUpdateDatePoliciesMsg, setLastUpdateDatePoliciesMsg] = useState("");

  useEffect(() => {
    getLatestUpdateDaily("/serviceLogs/buildings?service=db_buildings_unique").then((lastUpdateMsg) => {
      setLastUpdateDatePoliciesMsg(lastUpdateMsg);
    });

    return () => {
      setSelectedPolicyList([]); // Cleanup when tab is changed
    };
  }, [setSelectedPolicyList]);

  const getPoliciesData = useCallback(async () => {
    setTableState((old) => ({ ...old, isLoading: true }));

    try {
      const { data: policiesResponse } = await httpClient().get(
        `/terrashield/terraShieldPolicies?page=${paginationModel.page}&pageSize=${paginationModel.pageSize}&filters=${getFilterQuery(filterModel, activationCode)}&${getSortDetailsString(sortModel)}`,
      );

      setPoliciesData(
        policiesResponse.data.map((newPolicy: EmergencyPolicy) => {
          const selectedCurrPolicy = selectedPolicyList.find(
            (selectedPolicy) => selectedPolicy.policyNumber === newPolicy.policyNumber,
          );

          return { ...newPolicy, selected: selectedCurrPolicy?.selected || false }; // make sure selected policies are marked in the table after changing pages
        }),
      );

      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: policiesResponse.total,
      }));
    } catch (error) {
      apiError(error);

      setPoliciesData([]);
      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: 0,
      }));
    }
  }, [filterModel, paginationModel, sortModel, activationCode, selectedPolicyList]);

  useEffect(() => {
    if (!isThereAnyIncompleteFilter(filterModel)) {
      getPoliciesData();
    }
  }, [filterModel, getPoliciesData]);

  const downloadEmergencyPoliciesTableFunction = async () => {
    try {
      const { data: policiesResponse } = await httpClient().get(
        `/terrashield/terraShieldPolicies?page=0&pageSize=9999999&filters=${getFilterQuery(filterModel, activationCode)}&${getSortDetailsString(sortModel)}`,
      );

      return policiesResponse.data;
    } catch (error) {
      apiError(error);
      return [];
    }
  };

  const onFilterChange = useCallback((aFilterModel) => {
    setFilterModel(aFilterModel);
  }, []);

  const onSortChange = useCallback((aSortModel: GridSortModel) => {
    setSortModel(aSortModel);
  }, []);

  const onCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, row: EmergencyPolicy) => {
    const updatedData = policiesData.map((policy) => {
      // the other policies with the same policy number have the same location
      return policy.policyNumberSecondary === row.policyNumberSecondary &&
        policy.multilocationID === row.multilocationID
        ? { ...policy, selected: !row.selected }
        : { ...policy };
    });

    const selected = updatedData.filter((policy) => policy.selected);
    setSelectedPolicyList(selected);
    setPoliciesData(updatedData);
  };

  const policiesColumns = [
    {
      field: "selected",
      headerName: t("policy.action"),
      sortable: false,
      filterable: false,
      width: 50,
      renderCell: (params: GridValueGetterParams) => {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <FormControlLabel
              label=""
              control={
                <Checkbox
                  icon={<VisibilityOffRoundedIcon />}
                  checkedIcon={<VisibilityRoundedIcon />}
                  checked={params.row?.selected}
                  onChange={(event) => onCheckboxChange(event, params.row)}
                  color="primary"
                />
              }
            />
          </div>
        );
      },
    },
    {
      field: "companyCode",
      headerName: t("company"),
      ...buildingCompanyColumnFeatures,
      valueGetter: (params: GridValueGetterParams) =>
        getCompanyCodeName(params.row.companyCode, config.companyCodesMap),
    },
    {
      field: "policyNumberSecondary",
      headerName: t("policy.id"),
      width: 130,
    },
    {
      field: "belongsToFloodArea",
      headerName: "Flood",
      width: 70,
      renderCell: (params: GridRenderCellParams) => {
        return <span style={{ color: params.value === "TRUE" ? "red" : "blue" }}>{params.value}</span>;
      },

      type: "singleSelect",
      filterOperators: isSingleSelectOperators,
      valueOptions: [
        { value: "TRUE", label: "TRUE" },
        { value: "FALSE", label: "FALSE" },
      ],
    },
    {
      field: "policyNumber",
      headerName: t("policy.id"),
      width: 200,
    },
    {
      field: "multilocationID",
      headerName: t("idLocation"),
      width: 170,
    },
    {
      field: "productCommercial",
      headerName: t("product"),
      width: 200,
    },

    {
      field: "agencyCode",
      headerName: t("agencyCode"),
      width: 70,
    },
    {
      field: "agencyName",
      headerName: t("agency"),
      width: 150,
    },

    {
      field: "addressCity",
      headerName: t("city"),
      width: 150,
    },
    {
      field: "addressCityCode",
      headerName: t("cityCode"),
      width: 100,
    },
    {
      field: "addressStreetName",
      headerName: t("address"),
      width: 150,
      valueGetter: (params: GridValueGetterParams) =>
        tableCellRender(
          `${params.row.addressType ? params.row.addressType : ""} ${
            params.row.addressStreetName ? params.row.addressStreetName : ""
          } ${params.row.addressBuildingNo ? params.row.addressBuildingNo : ""}`,
        ),
    },
    {
      field: "addressZIPCode",
      headerName: t("zip"),
      width: 60,
    },
    { field: "addressProvinceCode", headerName: t("state"), width: 50 },
    {
      field: "insuredValue",
      headerName: t("climateAnalytics.insuredValue"),
      width: 100,
      filterOperators: numberFilterOperators,
      align: "right",
      ...fixedFormatter,
    },

    { field: "depth", headerName: t("depth"), width: 100 },
  ];

  const columns = policiesColumns.map((tableColumn) => ({
    valueFormatter: (params: GridValueFormatterParams) => tableCellRender(params.value?.toString()),
    filterOperators: stringFilterOperators,
    ...tableColumn,
  }));

  return (
    <Grid item xs={12} sm={12}>
      <DataGridCystellarTable
        columns={columns}
        rows={policiesData}
        rowCount={tableState.total}
        getRowId={(rowData: RowData) => rowData._id}
        onRowClick={(rowData: RowData) =>
          goToPolicyPage(
            rowData.row.policyNumber,
            rowData.row.policyNumberSecondary,
            rowData.row.multilocationID,
          )
        }
        loading={tableState.isLoading}
        paginationMode={"server"}
        paginationModel={paginationModel}
        pageSizeOptions={[10, 20, 50]}
        onPaginationModelChange={(newPaginationModel: GridPaginationModel) =>
          setPaginationModel(newPaginationModel)
        }
        filterMode={"server"}
        onFilterModelChange={onFilterChange}
        sortingMode={"server"}
        onSortModelChange={onSortChange}
        customToolbarComp={true}
        customToolbarCompMsg={lastUpdateDatePoliciesMsg}
        customToolbarWidgets={
          <>
            {getUserPermissionsList().includes(PermissionIdEnum.Super) && (
              <DownloadTableButton
                downloadTableFunction={downloadEmergencyPoliciesTableFunction}
                total={tableState.total}
                fileName="ContrattiEmergenza"
              ></DownloadTableButton>
            )}
          </>
        }
      />
    </Grid>
  );
};
